import styled from 'styled-components'
import { Box } from '../../atoms/Box'

// Re-export shared styles from SectionAnalysisStyles
export {
  SectionAnalysisTabsContainer as TabsContainer,
  SectionAnalysisTabButton as TabButton,
  SectionAnalysisContentContainer as ContentContainer,
  SectionAnalysisLoadingContainer as LoadingContainer,
  SectionAnalysisErrorContainer as ErrorContainer,
  SectionAnalysisTabContentContainer as TabContentContainer,
  SectionAnalysisMetricsGrid as MetricsGrid,
  SectionAnalysisChartGrid as ChartGrid,
  SectionAnalysisChartContainer as ChartContainer,
  SectionAnalysisChartTitle as ChartTitle,
  SectionAnalysisChartSubtitle as ChartSubtitle,
  SectionAnalysisInsightCard as InsightCard,
  SectionAnalysisInsightTitle as InsightTitle,
  SectionAnalysisInsightText as InsightText,
  SectionAnalysisFilterSection as FilterSection,
  SectionAnalysisFilterBar as FilterBar,
  SectionAnalysisLocationFilters as LocationFilters,
  SectionAnalysisFilterChip as FilterChip,
  SectionAnalysisLocationBadge as LocationBadge,
  SectionAnalysisDivider as Divider,
} from '../../molecules/SectionAnalysisStyles/SectionAnalysisStyles'

// Electricity-specific container
export const ElectricityContainer = styled(Box)`
  width: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  padding-bottom: 48px;
`

// Comparison card styles (same as TaxSection)
export const ComparisonCard = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  margin-bottom: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

export const ComparisonCardTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
`

export const ComparisonBarsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

export const ComparisonBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const ComparisonBarLabel = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  width: 96px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100px;
  }
`

export const ComparisonBarTrack = styled.div`
  flex: 1;
  height: 6px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: 3px;
  overflow: hidden;
`

export const ComparisonBar = styled.div<{
  $width: number
  $color: 'neutral' | 'success' | 'danger'
}>`
  height: 100%;
  width: ${({ $width }) => Math.min($width, 100)}%;
  background: ${({ theme, $color }) => {
    switch ($color) {
      case 'success':
        return theme.colors.success
      case 'danger':
        return theme.colors.danger
      default:
        return theme.colors.text.secondary
    }
  }};
  border-radius: 3px;
  transition: width 0.3s ease;
`

export const ComparisonBarValue = styled.span<{
  $color: 'neutral' | 'success' | 'danger'
}>`
  font-size: 11px;
  font-weight: 600;
  min-width: 64px;
  text-align: right;
  color: ${({ theme, $color }) => {
    switch ($color) {
      case 'success':
        return theme.colors.success
      case 'danger':
        return theme.colors.danger
      default:
        return theme.colors.text.primary
    }
  }};
`

export const ComparisonDiffText = styled.span<{ $positive: boolean }>`
  display: block;
  font-size: 10px;
  color: ${({ theme, $positive }) =>
    $positive ? theme.colors.success : theme.colors.danger};
  margin-left: 102px;
  margin-top: -5px;
  margin-bottom: 2px;

  @media (max-width: 768px) {
    margin-left: 108px;
  }
`

// Grid operator table styles
export const GridOperatorTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
`

export const GridOperatorTableHeader = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

  &:first-child {
    border-top-left-radius: ${({ theme }) => theme.borderRadius.medium};
  }

  &:last-child {
    border-top-right-radius: ${({ theme }) => theme.borderRadius.medium};
  }
`

export const GridOperatorTableRow = styled.tr<{ $highlighted?: boolean }>`
  background: ${({ theme, $highlighted }) =>
    $highlighted ? theme.colors.successLight : 'transparent'};

  &:hover {
    background: ${({ theme, $highlighted }) =>
      $highlighted
        ? theme.colors.successLight
        : theme.colors.background.secondary};
  }

  &:last-child td {
    border-bottom: none;
  }
`

export const GridOperatorTableCell = styled.td`
  padding: 10px 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

// Insight card styles
export const ElectricityInsightCard = styled.div<{
  $variant?: 'high' | 'medium' | 'low'
}>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'high':
        return theme.colors.successLight
      case 'medium':
        return theme.colors.warningLight
      case 'low':
        return theme.colors.background.tertiary
      default:
        return theme.colors.background.tertiary
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border-left: 3px solid
    ${({ theme, $variant }) => {
      switch ($variant) {
        case 'high':
          return theme.colors.success
        case 'medium':
          return theme.colors.warning
        case 'low':
          return theme.colors.danger
        default:
          return theme.colors.primary
      }
    }};
`

export const ElectricityInsightIcon = styled.span`
  font-size: 16px;
  flex-shrink: 0;
`

export const ElectricityInsightContent = styled.div`
  flex: 1;
`

export const ElectricityInsightTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 3px 0;
`

export const ElectricityInsightText = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  line-height: 1.4;
`

// Evolution summary
export const EvolutionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

export const EvolutionCard = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 12px;
  text-align: center;
`

export const EvolutionLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 3px;
`

export const EvolutionValue = styled.div<{ $positive?: boolean }>`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme, $positive }) =>
    $positive === undefined
      ? theme.colors.text.tertiary
      : $positive
        ? theme.colors.success
        : theme.colors.danger};
`
